import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Clock } from 'lucide-react';
import { authAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { isValidIndianMobileWithoutCountryCode, formatIndianMobile, getIndianMobileErrorMessage } from '../../utils/validation';
import { buildStayPricingPayload } from '../../utils/bookingPricing';
import { normalizeRoomBookingOption, roomBookingOptionLabel } from '../../utils/roomBookingOptions';

const LoginSignupModal = ({ isOpen, onClose, onSuccess, property, selectedRoom, dates }) => {
  const navigate = useNavigate();
  const { login } = useAuth();

  // Debug logging
  useEffect(() => {
    if (selectedRoom) {
      console.log('LoginSignupModal - selectedRoom:', selectedRoom);
      console.log('LoginSignupModal - property:', property);
      console.log('LoginSignupModal - dates:', dates);
      console.log('LoginSignupModal - selectedRoom type:', selectedRoom.type);
      console.log('LoginSignupModal - selectedRoom rooms:', selectedRoom.rooms);
      console.log('LoginSignupModal - first room:', selectedRoom.rooms?.[0]);
    }
  }, [selectedRoom, property, dates]);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [showOTP, setShowOTP] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [userDetails, setUserDetails] = useState({ name: '', email: '' });
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(30);
  const [activeInput, setActiveInput] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [showOtpFailureDialog, setShowOtpFailureDialog] = useState(false);
  const [otpFailureMessage, setOtpFailureMessage] = useState('');
  /** Prevents infinite verify calls: same 6-digit OTP must not auto-submit twice until the user edits the code. */
  const autoVerifiedOtpRef = useRef('');

  // Timer effect for OTP countdown
  useEffect(() => {
    let timer;
    if (showOTP && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [showOTP, timeLeft]);

  // Auto-submit when OTP is complete (do not depend on isProcessing — that caused infinite retries on failure)
  useEffect(() => {
    const code = otp.join('');
    if (!showOTP || code.length !== 6) {
      autoVerifiedOtpRef.current = '';
      return;
    }
    if (autoVerifiedOtpRef.current === code) {
      return;
    }
    autoVerifiedOtpRef.current = code;
    setIsProcessing(true);
    handleOTPSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only otp + showOTP; avoids loop on isProcessing
  }, [otp, showOTP]);

  const handleSendOTP = async () => {
    // Clear previous errors
    setError('');
    setPhoneError('');

    // Validate Indian phone number
    if (!isValidIndianMobileWithoutCountryCode(phoneNumber)) {
      setPhoneError(getIndianMobileErrorMessage(`+91${phoneNumber}`));
      return;
    }

    try {
      setIsProcessing(true);

      // Format mobile number in international format as expected by backend
      const internationalMobile = formatIndianMobile(phoneNumber);

      const response = await authAPI.sendOTP(internationalMobile);

      setIsExistingUser(response.isExistingUser);

      if (response.isExistingUser) {
        // Existing user - go directly to OTP
        setShowOTP(true);
        setTimeLeft(30);
        setOtp(['', '', '', '', '', '']);
        setActiveInput(0);
      } else {
        // New user - collect name and email first
        setShowUserDetails(true);
      }

    } catch (error) {
      console.error('Failed to send OTP:', error);
      setError(error.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOTPSubmit = async () => {
    const otpString = otp.join('');

    if (otpString.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      setIsProcessing(false);
      return;
    }

    try {
      setError('');
      setShowOtpFailureDialog(false);

      // Format mobile number in international format as expected by backend
      const internationalMobile = formatIndianMobile(phoneNumber);

      // For new users, include name and email in verification
      const verificationData = {
        mobile: internationalMobile,
        otp: otpString
      };

      if (!isExistingUser) {
        verificationData.name = userDetails.name;
        verificationData.email = userDetails.email;
      }

      const response = await authAPI.verifyOTP(verificationData.mobile, verificationData.otp, verificationData.name, verificationData.email);

      // Use auth context to handle login
      login(response.user, response.token);

      // Close modal and call success callback
      onClose();
      onSuccess && onSuccess();

      // Navigate to booking review with selected room data
      // Ensure dates are properly formatted strings, not Date objects
      const formatDatesForDisplay = (datesObj) => {
        if (!datesObj) {
          return {
            checkIn: "Wed, 30 Jun", // Fallback if no dates provided
            checkOut: "Thu, 31 Jun", // Fallback if no dates provided
            checkInTime: "2:00 PM",  // Fallback if no dates provided
            checkOutTime: "11:00 AM", // Fallback if no dates provided
            duration: "1 Night"      // Fallback if no dates provided
          };
        }

        // If dates are already formatted (from room selection), keep Date objects for night math
        if (typeof datesObj.checkIn === 'string' && typeof datesObj.checkOut === 'string') {
          return {
            ...datesObj,
            checkInDate: datesObj.checkInDate instanceof Date ? datesObj.checkInDate : datesObj.checkInDate ? new Date(datesObj.checkInDate) : null,
            checkOutDate: datesObj.checkOutDate instanceof Date ? datesObj.checkOutDate : datesObj.checkOutDate ? new Date(datesObj.checkOutDate) : null
          };
        }

        // If dates are Date objects, format them
        if (datesObj.checkIn instanceof Date && datesObj.checkOut instanceof Date) {
          const formatDate = (date) => {
            const options = { weekday: 'short', day: 'numeric', month: 'short' };
            return date.toLocaleDateString('en-US', options);
          };

          const diffTime = Math.abs(datesObj.checkOut - datesObj.checkIn);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          return {
            checkIn: formatDate(datesObj.checkIn),
            checkOut: formatDate(datesObj.checkOut),
            checkInDate: datesObj.checkIn,
            checkOutDate: datesObj.checkOut,
            checkInTime: datesObj.checkInTime || "2:00 PM",
            checkOutTime: datesObj.checkOutTime || "11:00 AM",
            duration: diffDays === 1 ? "1 Night" : `${diffDays} Nights`
          };
        }

        // Fallback
        return {
          checkIn: "Wed, 30 Jun",
          checkOut: "Thu, 31 Jun",
          checkInTime: "2:00 PM",
          checkOutTime: "11:00 AM",
          duration: "1 Night"
        };
      };

      const formattedDates = formatDatesForDisplay(dates);
      const roomsForPricing =
        selectedRoom?.type === 'multiple'
          ? selectedRoom.rooms || []
          : selectedRoom?.rooms?.length
            ? selectedRoom.rooms
            : selectedRoom
              ? [{ ...selectedRoom, quantity: selectedRoom.quantity || 1 }]
              : [];
      const pricing = buildStayPricingPayload(roomsForPricing, formattedDates);

      const bookingData = {
        room: selectedRoom?.type === 'multiple' ? selectedRoom.rooms[0] : selectedRoom,
        property: property,
        dates: formattedDates,
        pricing: {
          roomPrice: pricing.roomPrice,
          taxes: pricing.taxes,
          discount: 0,
          total: pricing.total
        },
        user: response.user,
        selectedRooms: roomsForPricing,
        guestData: selectedRoom?.guestData, // Include guest data for consistency
        // Add additional booking details
        bookingSummary: {
          totalRooms: selectedRoom?.type === 'multiple' ?
            selectedRoom.rooms.reduce((total, room) => total + room.quantity, 0) :
            selectedRoom?.rooms?.[0]?.quantity || 1,
          totalGuests: selectedRoom?.guestData ?
            selectedRoom.guestData.adults + selectedRoom.guestData.children :
            (selectedRoom?.type === 'multiple' ?
              selectedRoom.rooms.reduce((total, room) => total + (room.sleeps * room.quantity), 0) :
              (selectedRoom?.rooms?.[0]?.sleeps || 1) * (selectedRoom?.rooms?.[0]?.quantity || 1)),
          roomTypes: selectedRoom?.type === 'multiple' ?
            selectedRoom.rooms.map(room => ({
              name: room.name,
              quantity: room.quantity,
              option: normalizeRoomBookingOption(room.selectedOption),
              sleeps: room.sleeps,
              bedType: room.bedType,
              price: room.price,
              taxes: room.taxes
            })) :
            [{
              name: selectedRoom.rooms?.[0]?.name,
              quantity: selectedRoom.rooms?.[0]?.quantity || 1,
              option: normalizeRoomBookingOption(selectedRoom.rooms?.[0]?.selectedOption),
              sleeps: selectedRoom.rooms?.[0]?.sleeps,
              bedType: selectedRoom.rooms?.[0]?.bedType,
              price: selectedRoom.rooms?.[0]?.price,
              taxes: selectedRoom.rooms?.[0]?.taxes
            }]
        }
      };

      console.log('Booking data debug:', {
        selectedRoom,
        selectedRooms: selectedRoom?.type === 'multiple' ? selectedRoom.rooms : [selectedRoom.rooms[0]],
        pricing: bookingData.pricing,
        property: property
      });

      console.log('Final bookingData being sent to BookingReview:', bookingData);

      // Only navigate to booking review if we have booking data (coming from property selection)
      if (property && selectedRoom && dates) {
        const propertyId = property?.id;

        console.log('Navigation debug:', {
          propertyId,
          property,
          currentPath: window.location.pathname
        });

        navigate(`/property/${propertyId}/booking-review`, {
          state: {
            bookingData,
            fromRoomSelection: true
          }
        });
      }
      // If no booking data, stay on current page (user logged in from account page)

    } catch (error) {
      console.error('OTP verification failed:', error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Invalid OTP. Please try again.';
      setError(msg);
      setOtpFailureMessage(msg);
      setShowOtpFailureDialog(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleContinue = () => {
    handleSendOTP();
  };

  const handleUserDetailsSubmit = async () => {
    if (!userDetails.name.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!userDetails.email.trim()) {
      setError('Please enter your email');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userDetails.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setError('');
    try {
      setIsProcessing(true);
      const internationalMobile = formatIndianMobile(phoneNumber);
      await authAPI.sendSignupOTP(internationalMobile);
      setShowUserDetails(false);
      setShowOTP(true);
      setTimeLeft(30);
      setOtp(['', '', '', '', '', '']);
      setActiveInput(0);
    } catch (error) {
      console.error('Send signup OTP error:', error);
      setError(error.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOTP = [...otp];
      newOTP[index] = value;
      setOtp(newOTP);

      // Auto-focus next input
      if (value && index < 5) {
        setActiveInput(index + 1);
      }
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      setActiveInput(index - 1);
    }
  };

  const handleOTPPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const pastedOTP = pastedData.replace(/\D/g, '').slice(0, 6); // Only numbers, max 6 digits

    if (pastedOTP.length === 6) {
      const newOtp = pastedOTP.split('').concat(Array(6).fill('')).slice(0, 6);
      setOtp(newOtp);
      setActiveInput(5); // Focus on last input
    }
  };

  const handleResendOTP = () => {
    setTimeLeft(30);
    setOtp(['', '', '', '', '', '']);
    setActiveInput(0);
    autoVerifiedOtpRef.current = '';
    handleSendOTP();
  };

  if (!isOpen) return null;

  const dismissOtpFailureDialog = () => {
    setShowOtpFailureDialog(false);
    setOtp(['', '', '', '', '', '']);
    setActiveInput(0);
    autoVerifiedOtpRef.current = '';
  };

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 ${showOTP || showUserDetails
        ? 'flex items-center justify-center'
        : 'flex items-end md:items-center md:justify-center'
      }`}>
      <div className={`bg-[#FAF7F2] md:bg-white w-full relative transform transition-transform duration-300 ease-out ${showOTP || showUserDetails
          ? 'h-full  max-w-none md:max-w-md md:h-auto p-[20px] md:rounded-[24px]'
          : 'rounded-t-[24px] h-auto max-w-md mx-auto p-[20px] md:rounded-[24px]'
        }`}>



        {/* Header */}
        <div className="text-start flex items-center gap-[8px]">
          {/* Close Button - Always visible */}
        <button
          onClick={() => {
            onClose();
            // Reset all state
            setPhoneNumber('');
            setShowOTP(false);
            setShowUserDetails(false);
            setUserDetails({ name: '', email: '' });
            setOtp(['', '', '', '', '', '']);
            setActiveInput(0);
            setTimeLeft(30);
            setError('');
            setPhoneError('');
            setIsExistingUser(false);
            setShowOtpFailureDialog(false);
            setOtpFailureMessage('');
            autoVerifiedOtpRef.current = '';
          }}
          className=" hover:bg-gray-100 rounded-full transition-colors z-10 "
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <g clip-path="url(#clip0_2846_6751)">
                <path d="M5 12H19" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M5 12L11 18" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M5 12L11 6" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_2846_6751">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
        </button>
          <h2 className="text-[20px] font-medium tracking-tight text-black">
            {showOTP ? '' : showUserDetails ? 'Complete Your Profile' : 'Log in or Sign up'}
          </h2>
        </div>

        {/* Booking Summary Section */}
        {/*        {!showOTP && selectedRoom && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-[16px] font-medium text-gray-800 mb-3">Booking Summary</h3>
            
           
            <div className="mb-3 p-3 bg-white rounded border">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gray-200 rounded-lg overflow-hidden">
                  {property?.image && (
                    <img src={property.image} alt={property.name} className="w-full h-full object-cover" />
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{property?.name || 'Property'}</h4>
                  <p className="text-sm text-gray-600">{property?.city}, {property?.state}</p>
                </div>
              </div>
            </div>

      
            <div className="space-y-2">
              {selectedRoom?.type === 'multiple' && selectedRoom.rooms ? (
                // Multiple rooms selected
                selectedRoom.rooms.map((room, index) => (
                  <div key={index} className="p-3 bg-white rounded border flex items-center justify-between">
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900">{room.name || 'Room'}</h5>
                      <p className="text-sm text-gray-600">
                        {room.quantity || 1} × {roomBookingOptionLabel(room.selectedOption)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Sleeps: {room.sleeps || 'N/A'} • {room.bedType || 'N/A'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        ₹{((room.price || 0) * (room.quantity || 1)).toLocaleString()}
                      </p>
                      {room.taxes > 0 && (
                        <p className="text-xs text-gray-500">
                          Includes ₹{room.taxes} taxes
                        </p>
                      )}
                    </div>
                  </div>
                ))
              ) : selectedRoom?.type === 'single' && selectedRoom.rooms?.[0] ? (
                // Single room selected
                <div className="p-3 bg-white rounded border flex items-center justify-between">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">{selectedRoom.rooms[0].name}</h5>
                    <p className="text-sm text-gray-600">
                      {roomBookingOptionLabel(selectedRoom.rooms[0].selectedOption)}
                    </p>
                    <p className="text-sm text-gray-600">
                      Sleeps: {selectedRoom.rooms[0].sleeps || 'N/A'} • {selectedRoom.rooms[0].bedType || 'N/A'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">
                      ₹{(selectedRoom.rooms[0].price || 0).toLocaleString()}
                    </p>
                    {selectedRoom.rooms[0].taxes > 0 && (
                      <p className="text-xs text-gray-500">
                        Includes ₹{selectedRoom.rooms[0].taxes} taxes
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-white rounded border text-center text-gray-500">
                  No room details available
                </div>
              )}
            </div>

           
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">Total:</span>
                <span className="text-lg font-bold text-gray-900">
                  ₹{selectedRoom?.type === 'multiple' && selectedRoom.rooms ? 
                    selectedRoom.rooms.reduce((total, room) => 
                      total + ((room.price || 0) * (room.quantity || 1)), 0
                    ).toLocaleString() : 
                    selectedRoom?.type === 'single' && selectedRoom.rooms?.[0] ?
                    ((selectedRoom.rooms[0].price || 0) * (selectedRoom.rooms[0].quantity || 1)).toLocaleString() :
                    '0'
                  }
                </span>
              </div>
              <p className="text-sm text-gray-500 text-center mt-2">
                {selectedRoom?.type === 'multiple' && selectedRoom.rooms ? 
                  `${selectedRoom.rooms.reduce((total, room) => total + (room.quantity || 1), 0)} rooms selected` : 
                  selectedRoom?.type === 'single' && selectedRoom.rooms?.[0] ?
                  `${selectedRoom.rooms[0].quantity || 1} room selected` :
                  '0 rooms selected'
                }
              </p>
            </div>
          </div>
        )}
        */}

        {/* Error Display */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {!showOTP && !showUserDetails ? (
          <>
            {/* Mobile Number Input Section */}
            <div className="mb-6 mt-[16px]">
              <div className="flex space-x-[8px] mb-[16px]">
                {/* Country Code Selector */}
                <div className="flex items-center justify-center p-[16px] border border-[#d9d9d9] rounded-[14px] bg-white min-w-[80px]">
                  <span className="mr-[4px]">🇮🇳</span>
                  <span className="text-black font-normal">{countryCode}</span>
                </div>

                {/* Mobile Number Input */}
                <input
                  type="tel"
                  placeholder="Enter 10 digit mobile number"
                  value={phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setPhoneNumber(value);
                    // Clear phone error when user starts typing
                    if (phoneError) {
                      setPhoneError('');
                    }
                  }}
                  className={`flex-1 p-[16px] border rounded-[14px] bg-white placeholder-[#d9d9d9] focus:outline-none focus:ring-1 focus:border-transparent ${phoneError
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-[#d9d9d9] focus:ring-[#d9d9d9]'
                    }`}
                  maxLength={10}
                />
              </div>

              {/* Phone Error Display */}
              {phoneError && (
                <div className="mt-2 text-red-600 text-sm">
                  {phoneError}
                </div>
              )}

              {/* Continue Button */}
              <button
                onClick={handleContinue}
                disabled={phoneNumber.length !== 10 || isProcessing || phoneError}
                className={`w-full py-[14px] rounded-[14px] font-medium transition-colors ${phoneNumber.length === 10 && !isProcessing && !phoneError
                    ? 'bg-[#131313] text-white'
                    : 'bg-[#13131340] text-white cursor-not-allowed'
                  }`}
              >
                {isProcessing ? 'Sending OTP...' : 'Continue'}
              </button>
            </div>
          </>
        ) : showUserDetails ? (
          <>
            <div className=" h-full">
              {/* User Details Input Section */}
              <div className="mb-6 mt-[16px]">
                <div className="space-y-4">
                  {/* Name Input */}
                  <div>
                    <label className="block text-[14px] font-medium text-gray-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      placeholder="Enter your full name"
                      value={userDetails.name}
                      onChange={(e) => setUserDetails({ ...userDetails, name: e.target.value })}
                      className="w-full p-[16px] border border-[#d9d9d9] rounded-[14px] bg-white placeholder-[#d9d9d9] focus:outline-none focus:ring-1 focus:ring-[#d9d9d9] focus:border-transparent"
                    />
                  </div>

                  {/* Email Input */}
                  <div>
                    <label className="block text-[14px] font-medium text-gray-700 mb-2">Email Address</label>
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      value={userDetails.email}
                      onChange={(e) => setUserDetails({ ...userDetails, email: e.target.value })}
                      className="w-full p-[16px] border border-[#d9d9d9] rounded-[14px] bg-white placeholder-[#d9d9d9] focus:outline-none focus:ring-1 focus:ring-[#d9d9d9] focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Continue Button */}
                <button
                  onClick={handleUserDetailsSubmit}
                  disabled={
                    isProcessing ||
                    !userDetails.name.trim() ||
                    !userDetails.email.trim()
                  }
                  className={`w-full py-[14px] rounded-[14px] font-medium transition-colors mt-6 ${userDetails.name.trim() && userDetails.email.trim() && !isProcessing
                      ? 'bg-[#131313] text-white'
                      : 'bg-[#13131340] text-white cursor-not-allowed'
                    }`}
                >
                  {isProcessing ? 'Sending OTP...' : 'Continue to Verification'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col items-center justify-center bg-[#FAF7F2] md:bg-[#fff] p-[16px] h-full md:rounded-[24px]">
              {/* OTP Verification Section */}
              <div className="text-center w-full">
                {/* Instructional Text */}
                <div className="text-start tracking-tight">
                  <p className="text-black text-[20px] md:font-medium mb-[20px]">Enter Verification Code</p>
                  <p className="text-[#bcbcbc] font-normal text-[14px]">
                    We have sent a verification code to{' '}
                  </p>
                  <p className="font-medium mb-[20px] text-black">{countryCode} {phoneNumber}</p>


                </div>

                {/* OTP Input Fields */}
                <div className="mb-[20px]">
                  {/* Hidden Input for OTP Entry */}
                  <input
                    type="text"
                    value={otp.join('')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      const newOtp = value.split('').concat(Array(6).fill('')).slice(0, 6);
                      setOtp(newOtp);
                      setActiveInput(Math.min(value.length, 5));
                    }}
                    onPaste={handleOTPPaste}
                    onFocus={() => setActiveInput(otp.join('').length)}
                    className="absolute opacity-0 pointer-events-none"
                    maxLength={6}
                    inputMode="numeric"
                    autoFocus
                  />

                  {/* Visual OTP Display */}
                  <div className="flex justify-center space-x-[8px]">
                    {otp.map((digit, index) => (
                      <div
                        key={index}
                        className={`w-full h-[56px] flex items-center justify-center text-[18px] font-normal border-[1px] rounded-[14px] transition-colors cursor-pointer ${index === activeInput
                            ? 'border-black'
                            : digit
                              ? 'border-[#e3e3e3] '
                              : 'border-[#e3e3e3] '
                          }`}
                        onClick={() => {
                          setActiveInput(index);
                          // Focus the hidden input and move cursor to position
                          const hiddenInput = document.querySelector('input[type="text"][class*="opacity-0"]');
                          if (hiddenInput) {
                            hiddenInput.focus();
                            hiddenInput.setSelectionRange(index, index);
                          }
                        }}
                      >
                        {digit || (index === activeInput ? '|' : '')}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Processing Indicator */}
                {isProcessing && (
                  <div className="text-center mb-4">
                    <div className="inline-flex items-center space-x-2 text-[#131313]">
                      <div className="w-4 h-4 border-2 border-[#131313] border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-[14px]">Verifying OTP...</span>
                    </div>
                  </div>
                )}

                {/* Timer and Resend */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-[4px] text-[#131313]">
                    <Clock size={16} />
                    <span className="text-[14px]">
                      {Math.floor(timeLeft / 60).toString().padStart(2, '0')}:
                      {(timeLeft % 60).toString().padStart(2, '0')}
                    </span>
                  </div>
                  {timeLeft === 0 && (
                    <button
                      onClick={handleResendOTP}
                      className="text-blue-600 text-[14px] font-normal hover:text-blue-700 ml-4"
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
              </div>
            </div>

          </>
        )}
      </div>

      {showOtpFailureDialog && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="otp-fail-title"
        >
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center">
            <h3 id="otp-fail-title" className="text-lg font-semibold text-gray-900 mb-2">
              OTP verification failed
            </h3>
            <p className="text-sm text-gray-600 mb-6">{otpFailureMessage}</p>
            <button
              type="button"
              onClick={dismissOtpFailureDialog}
              className="w-full py-3 rounded-xl bg-[#131313] text-white font-medium hover:bg-black transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginSignupModal;
